import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Redirect, useLocation } from "react-router-dom";
import { useStaffSession, describeRole } from "../session/staffSession";
import { staffDisplayName } from "../../util/formatApiError";
import { Spinner } from "./common";
import "../staff.css";

/**
 * Authentication gate + chrome for every staff page.
 * The header keeps the few links used on every screen.
 * The drawer holds the full menu and the current section.
 */
const StaffLayout = function (props) {
  const session = useStaffSession();
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  const navLinks = useMemo(
    function () {
      const caps = session.caps;
      if (caps.isBookingViewerOnly && caps.canViewBookingAnalytics) {
        return [
          { to: "/staff/dashboard", label: "Dashboard" },
          { to: "/staff/scan", label: "Scan QR" },
          { to: "/staff/bookings", label: "Participants" },
          { to: "/staff/game-status", label: "Game status" },
          { to: "/staff/profile", label: "Profile" },
          { to: "/events", label: "Events" },
        ];
      }
      const links = [
        { to: "/staff", label: "Home", exact: true },
        { to: "/staff/scan", label: "Scan QR" },
      ];
      if (caps.canOperateValet) links.push({ to: "/staff/valet", label: "Valet" });
      if (caps.canOperateCart) links.push({ to: "/staff/cart", label: "Cart" });
      if (caps.canOperateVendorPay) links.push({ to: "/staff/vendorpay", label: "VendorPay" });
      if (caps.canManageAccreditation) links.push({ to: "/staff/accreditation", label: "Accreditation" });
      if (caps.canViewBookingAnalytics) {
        links.push({ to: "/staff/dashboard", label: "Dashboard" });
        links.push({ to: "/staff/bookings", label: "Participants" });
        links.push({ to: "/staff/game-status", label: "Game status" });
      } else if (caps.isCommitteeDesk || caps.canRecordCash || caps.isAdmin) {
        links.push({ to: "/staff/game-status", label: "Game status" });
      }
      if (caps.canViewSponsors) links.push({ to: "/staff/sponsors", label: "Sponsors" });
      if (caps.canRecordCash) links.push({ to: "/staff/cash", label: "Offline cash" });
      if (caps.isAdmin) {
        links.push({ to: "/staff/committees", label: "Committees" });
        links.push({ to: "/staff/accounts", label: "Admins" });
      }
      if (caps.canViewSiteFeedback) links.push({ to: "/staff/feedback", label: "Site feedback" });
      links.push({ to: "/staff/profile", label: "Profile" });
      links.push({ to: "/events", label: "Events" });
      return links;
    },
    [session.caps]
  );

  useEffect(
    function () {
      setNavOpen(false);
    },
    [location.pathname, location.search]
  );

  useEffect(
    function () {
      if (!navOpen) return undefined;
      function onKey(event) {
        if (event.key === "Escape") setNavOpen(false);
      }
      var previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      document.addEventListener("keydown", onKey);
      return function () {
        document.body.style.overflow = previous;
        document.removeEventListener("keydown", onKey);
      };
    },
    [navOpen]
  );

  if (session.status === "loading" || session.status === "idle") {
    return (
      <div className="stf-shell stf-shell--loading">
        <div className="stf-loading-center">
          <Spinner size={28} />
          <span>Loading session…</span>
        </div>
      </div>
    );
  }
  if (session.status === "unauthenticated") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Redirect to={"/staff/login?next=" + next} />;
  }

  const staff = session.staff || {};
  const roleLabel = describeRole(staff.role_code);
  const displayName = staffDisplayName(staff);
  const subNav = buildSubNav(location.pathname, session.caps);
  const pageLabel = currentPageLabel(location.pathname, navLinks, subNav);
  const barLinks = headerLinks(session.caps || {});
  const menuGroups = groupLinks(navLinks);
  const mark = initials(displayName);

  return (
    <div className="stf-shell">
      <header className="stf-topbar">
        <button
          type="button"
          className={"stf-topbar__menu" + (navOpen ? " stf-topbar__menu--open" : "")}
          aria-expanded={navOpen}
          aria-controls="stf-nav-drawer"
          aria-label={navOpen ? "Close menu" : "Open menu"}
          onClick={function () {
            setNavOpen(function (v) {
              return !v;
            });
          }}
        >
          <span className="stf-topbar__menu-icon" />
        </button>
        <Link to="/staff" className="stf-topbar__brand">
          <span className="stf-topbar__mark" aria-hidden="true">P</span>
          <span className="stf-topbar__brand-text">
            ParkAdda
            <small>Staff</small>
          </span>
        </Link>
        <nav className="stf-topbar__nav" aria-label="Primary">
          {barLinks.map(function (l) {
            return (
              <NavLink
                key={"bar-" + l.to}
                to={l.to}
                exact={!!l.exact}
                activeClassName="stf-topbar__nav--active"
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="stf-topbar__current">{pageLabel}</div>
        <div className="stf-topbar__spacer" />
        <Link to="/staff/profile" className="stf-topbar__staff" title="Open profile">
          <span className="stf-avatar" aria-hidden="true">{mark}</span>
          <span className="stf-topbar__staff-text">
            <strong>{displayName}</strong>
            <small>{roleLabel}</small>
          </span>
        </Link>
        <button
          className="stf-topbar__logout"
          type="button"
          onClick={function () {
            session.logout();
          }}
        >
          Log out
        </button>
      </header>

      {subNav.length > 0 ? (
        <nav className="stf-subnav" aria-label="This section">
          {subNav.map(function (l) {
            return (
              <NavLink
                key={"sub-" + l.to}
                to={l.to}
                exact={!!l.exact}
                className="stf-subnav__link"
                activeClassName="stf-subnav__link--active"
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>
      ) : null}

      <div
        id="stf-nav-drawer"
        className={"stf-drawer" + (navOpen ? " stf-drawer--open" : "")}
        aria-hidden={!navOpen}
      >
        <div
          className="stf-drawer__backdrop"
          onClick={function () {
            setNavOpen(false);
          }}
        />
        <div className="stf-drawer__panel" role="dialog" aria-label="Staff menu">
          <div className="stf-drawer__head">
            <div>
              <div className="stf-drawer__brand">ParkAdda</div>
              <div className="stf-drawer__brand-sub">Staff menu</div>
            </div>
            <button
              type="button"
              className="stf-drawer__close"
              aria-label="Close menu"
              onClick={function () {
                setNavOpen(false);
              }}
            >
              <span />
            </button>
          </div>
          <div className="stf-drawer__scroll">
            {menuGroups.map(function (group) {
              return (
                <div key={group.key} className="stf-drawer__group">
                  <div className="stf-drawer__title">{group.title}</div>
                  {group.items.map(function (l) {
                    return (
                      <NavLink
                        key={"drawer-" + l.to}
                        to={l.to}
                        exact={!!l.exact}
                        activeClassName="stf-drawer__link--active"
                        className="stf-drawer__link"
                        onClick={function () {
                          setNavOpen(false);
                        }}
                      >
                        {l.label}
                      </NavLink>
                    );
                  })}
                </div>
              );
            })}
            {subNav.length > 0 ? (
              <div className="stf-drawer__group">
                <div className="stf-drawer__title">This section</div>
                {subNav.map(function (l) {
                  return (
                    <NavLink
                      key={"section-" + l.to}
                      to={l.to}
                      exact={!!l.exact}
                      activeClassName="stf-drawer__link--active"
                      className="stf-drawer__link"
                      onClick={function () {
                        setNavOpen(false);
                      }}
                    >
                      {l.label}
                    </NavLink>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="stf-drawer__footer">
            <Link
              to="/staff/profile"
              className="stf-drawer__who"
              onClick={function () {
                setNavOpen(false);
              }}
            >
              <span className="stf-avatar stf-avatar--navy" aria-hidden="true">{mark}</span>
              <span>
                <strong>{displayName}</strong>
                <span>{roleLabel}</span>
              </span>
            </Link>
            <button
              type="button"
              className="stf-drawer__logout"
              onClick={function () {
                setNavOpen(false);
                session.logout();
              }}
            >
              Log out
            </button>
          </div>
        </div>
      </div>

      <main className="stf-page">{props.children}</main>
    </div>
  );
};

function initials(name) {
  var parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "PA";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function groupLinks(links) {
  var events = {
    "/staff/dashboard": true,
    "/staff/bookings": true,
    "/staff/game-status": true,
    "/staff/cash": true,
    "/staff/sponsors": true,
    "/staff/committees": true,
    "/staff/accounts": true,
    "/staff/feedback": true,
  };
  var account = { "/staff/profile": true, "/events": true };
  var groups = [
    { key: "work", title: "On the ground", items: [] },
    { key: "events", title: "Event desk", items: [] },
    { key: "account", title: "Account", items: [] },
  ];
  links.forEach(function (link) {
    if (account[link.to]) groups[2].items.push(link);
    else if (events[link.to]) groups[1].items.push(link);
    else groups[0].items.push(link);
  });
  return groups.filter(function (group) {
    return group.items.length > 0;
  });
}

function headerLinks(caps) {
  caps = caps || {};
  if (caps.isBookingViewerOnly && caps.canViewBookingAnalytics) {
    return [
      { to: "/staff/dashboard", label: "Dashboard", exact: true },
      { to: "/staff/scan", label: "Scan" },
      { to: "/staff/bookings", label: "Participants" },
      { to: "/staff/game-status", label: "Results" },
    ];
  }
  var links = [
    { to: "/staff", label: "Home", exact: true },
    { to: "/staff/scan", label: "Scan" },
  ];
  if (caps.canViewBookingAnalytics) {
    links.push({ to: "/staff/bookings", label: "Participants" });
    links.push({ to: "/staff/game-status", label: "Results" });
  } else if (caps.isCommitteeDesk || caps.canRecordCash || caps.isAdmin) {
    links.push({ to: "/staff/game-status", label: "Results" });
  }
  if (caps.canOperateValet) links.push({ to: "/staff/valet", label: "Valet" });
  if (caps.canOperateCart) links.push({ to: "/staff/cart", label: "Cart" });
  if (caps.canOperateVendorPay) links.push({ to: "/staff/vendorpay", label: "VendorPay" });
  return links.slice(0, 5);
}

function currentPageLabel(pathname, navLinks, subNav) {
  var best = "";
  var label = "Staff";
  var lists = [subNav, navLinks];
  for (var n = 0; n < lists.length; n++) {
    var links = lists[n];
    for (var i = 0; i < links.length; i++) {
      var to = links[i].to;
      var hit = pathname === to || (to !== "/staff" && pathname.indexOf(to) === 0);
      if (hit && to.length >= best.length) {
        best = to;
        label = links[i].label;
      }
    }
  }
  return label;
}

function buildSubNav(pathname, caps) {
  if (pathname.indexOf("/staff/valet") === 0 && caps.canOperateValet) {
    var links = [
      { to: "/staff/valet", label: "Dashboard", exact: true },
      { to: "/staff/valet/transactions", label: "Transactions" },
      { to: "/staff/valet/scan", label: "Scan / Intake" },
      { to: "/staff/valet/pos-exit", label: "POS exit" },
      { to: "/staff/valet/retrievals", label: "Retrievals" },
      { to: "/staff/valet/keys", label: "Keys" }
    ];
    if (caps.isAdmin) links.push({ to: "/staff/valet/admin", label: "Admin" });
    return links;
  }
  if (pathname.indexOf("/staff/cart") === 0 && caps.canOperateCart) {
    var links2 = [
      { to: "/staff/cart", label: "Dashboard", exact: true },
      { to: "/staff/cart/requests", label: "Requests" },
      { to: "/staff/cart/dispatch", label: "Dispatch" },
      { to: "/staff/cart/scan", label: "Scan / Dispatch" },
      { to: "/staff/cart/pos-exit", label: "POS exit" },
      { to: "/staff/cart/batches", label: "Batches" }
    ];
    if (caps.isAdmin) links2.push({ to: "/staff/cart/admin", label: "Admin" });
    return links2;
  }
  if (pathname.indexOf("/staff/vendorpay") === 0 && caps.canOperateVendorPay) {
    var links3 = [{ to: "/staff/vendorpay", label: "Dashboard", exact: true }];
    if (caps.canBillVendorPay) links3.push({ to: "/staff/vendorpay/pos", label: "POS" });
    links3.push({ to: "/staff/vendorpay/orders", label: "Orders" });
    if (caps.canFulfilVendorPay) links3.push({ to: "/staff/vendorpay/scan", label: "Scan" });
    links3.push({ to: "/staff/vendorpay/stock", label: "Stock" });
    if (caps.isAdmin) links3.push({ to: "/staff/vendorpay/admin", label: "Admin" });
    if (caps.isAdmin) links3.push({ to: "/staff/vendorpay/settlements", label: "Settlements" });
    return links3;
  }
  return [];
}

export default StaffLayout;
